export const CONSENT_KEY = "trinity-consent"
export const CONSENT_VERSION = 2
// Product policy: ask again after 180 days, or when the schema version changes.
export const CONSENT_LIFETIME = 180 * 24 * 60 * 60 * 1000
export const DENIED = Object.freeze({
  analytics: false,
  externalContent: false,
})

// A failed save must not let a readable, stale grant win on the next sync.
// Only a subsequent explicit, successfully persisted choice clears this latch.
let sessionBlocked = false
export const isConsentSessionBlocked = () => sessionBlocked

// Route hooks and the mounted provider must consume the same storage result.
const listeners = new Set()
export function subscribeConsentChanges(listener) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function refreshConsent() {
  const current = readConsent()
  listeners.forEach(listener => listener(current))
  return current
}

export function preserveConsentRecord(previous, next) {
  return JSON.stringify(previous) === JSON.stringify(next) ? previous : next
}

export function restoreConsentFocus(opener) {
  if (typeof document === "undefined") return
  const target = opener?.isConnected
    ? opener
    : document.getElementById("consent-open-preferences") ||
      document.getElementById("consent-settings-button") ||
      document.getElementById("consent-heading")
  target?.focus({ preventScroll: true })
}

export function parseConsent(raw, now = Date.now()) {
  try {
    const value = JSON.parse(raw)
    if (!value || value.version !== CONSENT_VERSION) return null
    if (Object.keys(DENIED).some(key => typeof value[key] !== "boolean")) {
      return null
    }
    const updated = Date.parse(value.updatedAt)
    const expires = Date.parse(value.expiresAt)
    if (
      !Number.isFinite(updated) ||
      !Number.isFinite(expires) ||
      updated > now ||
      expires <= now ||
      expires <= updated ||
      expires - updated > CONSENT_LIFETIME
    )
      return null
    return {
      version: CONSENT_VERSION,
      ...Object.fromEntries(Object.keys(DENIED).map(key => [key, value[key]])),
      updatedAt: value.updatedAt,
      expiresAt: value.expiresAt,
    }
  } catch {
    return null
  }
}

export function readConsent() {
  if (typeof window === "undefined") return { record: null, available: false }
  if (sessionBlocked) return { record: null, available: false }
  try {
    return {
      record: parseConsent(window.localStorage.getItem(CONSENT_KEY)),
      available: true,
    }
  } catch {
    sessionBlocked = true
    return { record: null, available: false }
  }
}

export function saveConsent(choices, now = Date.now()) {
  if (typeof window === "undefined") return null
  const record = {
    version: CONSENT_VERSION,
    ...Object.fromEntries(
      Object.keys(DENIED).map(key => [key, choices[key] === true])
    ),
    updatedAt: new Date(now).toISOString(),
    expiresAt: new Date(now + CONSENT_LIFETIME).toISOString(),
  }
  try {
    const serialized = JSON.stringify(record)
    window.localStorage.setItem(CONSENT_KEY, serialized)
    if (window.localStorage.getItem(CONSENT_KEY) !== serialized)
      throw new Error("Storage unavailable")
    sessionBlocked = false
    return record
  } catch {
    sessionBlocked = true
    // Do not leave an older grant behind when saving a rejection fails.
    try {
      window.localStorage.removeItem(CONSENT_KEY)
    } catch {
      /* inaccessible */
    }
    return null
  }
}
